import React from 'react';

export const MAX_LOGO_BYTES = 512 * 1024; // keep in sync with backend/handlers maxLogoBytes

// Reads a File into a "data:<mime>;base64,..." string.
export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export const emptySchoolForm = {
  id: '',
  name: '',
  short_name: '',
  price_usd: '',
  venmo_username: '',
  grade_options: [],
  ask_teacher: false,
  from_email_name: '',
  contact_email: '',
  // Logo state is kept separate from the rest of the form (which maps
  // directly onto the request body) because it has three distinct
  // outcomes on submit: leave alone (logoBase64 empty, logoRemoved false),
  // replace (logoBase64 set), or clear (logoRemoved true) — see callers'
  // handleSubmit.
  logoPreview: '',     // data URI shown in the form — either the existing logo (edit mode) or a freshly-picked file
  logoBase64: '',       // raw base64 (no "data:" prefix) of a newly-picked file, sent as logo_base64
  logoContentType: '',  // MIME type of a newly-picked file, sent as logo_content_type
  logoRemoved: false    // true once the admin clicks "Remove logo" on an existing logo
};

// Builds a school's form state from a school record returned by the API
// (GetAdminSchools/CreateSchool/UpdateSchool) — shared by Schools.js's
// edit mode and SchoolSettings.js.
export function schoolToFormData(school) {
  return {
    id: school.id,
    name: school.name || '',
    short_name: school.short_name || '',
    price_usd: school.price_usd != null ? String(school.price_usd) : '',
    venmo_username: school.venmo_username || '',
    grade_options: school.grade_options || [],
    ask_teacher: !!school.ask_teacher,
    from_email_name: school.from_email_name || '',
    contact_email: school.contact_email || '',
    logoPreview: school.logo_data_uri || '',
    logoBase64: '',
    logoContentType: '',
    logoRemoved: false
  };
}

// Turns form state into the request body expected by
// handlers.CreateSchool/UpdateSchool. id is included since CreateSchool
// needs it — callers editing an existing school (where id is immutable)
// simply ignore it or omit it from the URL as appropriate.
export function schoolFormToPayload(formData) {
  const payload = {
    id: formData.id,
    name: formData.name,
    short_name: formData.short_name,
    price_usd: parseFloat(formData.price_usd) || 0,
    venmo_username: formData.venmo_username,
    grade_options: formData.grade_options,
    ask_teacher: formData.ask_teacher,
    from_email_name: formData.from_email_name,
    contact_email: formData.contact_email
  };
  if (formData.logoBase64) {
    payload.logo_base64 = formData.logoBase64;
    payload.logo_content_type = formData.logoContentType;
  } else if (formData.logoRemoved) {
    payload.logo_removed = true;
  }
  return payload;
}

// The school-properties form fields shared by Schools.js (FullAdmin:
// create + edit, id editable only on create) and SchoolSettings.js
// (SchoolAdmin: edit their own school's properties only, id never shown).
// Purely presentational — all state lives in the caller, passed in via
// formData/setFormData plus a couple of derived-input helpers.
function SchoolPropertiesForm({
  formData,
  setFormData,
  showIdField = false,
  idEditable = false,
  gradeInput,
  setGradeInput,
  onAddGrade,
  onRemoveGrade,
  onLogoChange,
  onRemoveLogo
}) {
  return (
    <>
      {showIdField && (
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            School ID (used in URLs and as the subdomain, e.g. "lincoln-elem"):
          </label>
          <input
            type="text"
            value={formData.id}
            onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toLowerCase() }))}
            required
            disabled={!idEditable} // Renaming would break /admin/:schoolId links, historical orders, and the school's subdomain
            pattern="[a-z0-9-]+"
            placeholder="lincoln-elem"
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: idEditable ? 'white' : '#f8f9fa'
            }}
          />
          <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.35rem', marginBottom: 0 }}>
            This school will be reachable at "{formData.id || '<id>'}.&lt;your configured domain&gt;" — no separate domain to set up.
          </p>
        </div>
      )}

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Name:
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          required
          placeholder="Lincoln Elementary School"
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Short Name:
        </label>
        <input
          type="text"
          value={formData.short_name}
          onChange={(e) => setFormData(prev => ({ ...prev, short_name: e.target.value }))}
          placeholder="Lincoln"
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Logo:
        </label>
        {formData.logoPreview ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
            <img
              src={formData.logoPreview}
              alt="Logo preview"
              style={{ height: '3rem', width: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '0.25rem', backgroundColor: 'white' }}
            />
            <button
              type="button"
              onClick={onRemoveLogo}
              style={{ padding: '0.4rem 0.75rem', backgroundColor: 'white', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
            >
              Remove logo
            </button>
          </div>
        ) : (
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            onChange={onLogoChange}
            style={{ width: '100%' }}
          />
        )}
        <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.35rem', marginBottom: 0 }}>
          Shown next to the school name on the order page. PNG, JPEG, GIF, WEBP, or SVG, up to {Math.round(MAX_LOGO_BYTES / 1024)}KB.
        </p>
      </div>

      <div style={{ marginBottom: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Price (USD):
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.price_usd}
            onChange={(e) => setFormData(prev => ({ ...prev, price_usd: e.target.value }))}
            required
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Venmo Username:
          </label>
          <input
            type="text"
            value={formData.venmo_username}
            onChange={(e) => setFormData(prev => ({ ...prev, venmo_username: e.target.value }))}
            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Grade Options:
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <input
            type="text"
            value={gradeInput}
            onChange={(e) => setGradeInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddGrade(); } }}
            placeholder="e.g. 3rd"
            style={{ flex: 1, padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <button type="button" onClick={onAddGrade} style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: 'white' }}>
            Add
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {formData.grade_options.map(g => (
            <span key={g} style={{ padding: '0.25rem 0.6rem', backgroundColor: '#e9ecef', borderRadius: '12px', fontSize: '0.85rem' }}>
              {g} <button type="button" onClick={() => onRemoveGrade(g)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}>×</button>
            </span>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={formData.ask_teacher}
            onChange={(e) => setFormData(prev => ({ ...prev, ask_teacher: e.target.checked }))}
          />
          <span style={{ fontWeight: 'bold' }}>Ask for teacher in the wizard</span>
        </label>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Confirmation Email "From" Name:
        </label>
        <input
          type="text"
          value={formData.from_email_name}
          onChange={(e) => setFormData(prev => ({ ...prev, from_email_name: e.target.value }))}
          placeholder="Lincoln Elementary Lunchday"
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Contact Email:
        </label>
        <input
          type="email"
          value={formData.contact_email}
          onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
        />
      </div>
    </>
  );
}

export default SchoolPropertiesForm;
